"use client";

import React, { useCallback, useEffect, useId } from "react";
import type { IconType } from "react-icons";
import {
  FiCheck,
  FiCopy,
  FiDownload,
  FiExternalLink,
  FiMoreHorizontal,
  FiMoreVertical,
  FiPlusSquare,
  FiShare,
  FiX,
} from "react-icons/fi";
import { useDialogFocusTrap } from "../../ui/useDialogFocusTrap";
import { useOptionalToast } from "../../ui/ToastProvider";
import { getInstallSteps, type InstallStepIcon } from "../../../lib/pwa/installSteps";
import type { InstallPlatform } from "../../../lib/pwa/platform";

/**
 * "Abrir no Chrome" via `intent://` (INAPP-03, P2) só entra depois da
 * validação em aparelho. Até lá o botão não é renderizado.
 */
export const ENABLE_ANDROID_INTENT_HANDOFF = false;

/** Ícone Feather de cada passo. Também usado pela página `/{slug}/app`. */
export const INSTALL_STEP_ICONS: Record<InstallStepIcon, IconType> = {
  FiMoreHorizontal,
  FiShare,
  FiCheck,
  FiMoreVertical,
  FiPlusSquare,
  FiDownload,
};

export type InstallGuideSheetProps = {
  open: boolean;
  onClose(): void;
  /** Nome do app no título (GUIA-02). */
  appName: string;
  platform: InstallPlatform;
  /** Navegador interno de app (INAPP-02). */
  inApp: boolean;
};

function buildChromeIntentUrl() {
  const { host, pathname, search } = window.location;
  return `intent://${host}${pathname}${search}#Intent;scheme=https;package=com.android.chrome;end`;
}

export function InstallGuideSheet({
  open,
  onClose,
  appName,
  platform,
  inApp,
}: InstallGuideSheetProps) {
  const titleId = useId();
  const panelRef = useDialogFocusTrap<HTMLDivElement>(open);
  const toast = useOptionalToast();

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast?.success("Link copiado");
    } catch {
      toast?.error("Não foi possível copiar. Copie o endereço pela barra do navegador.");
    }
  }, [toast]);

  if (!open) return null;

  const content = getInstallSteps(platform, inApp);
  const showOpenInChrome = ENABLE_ANDROID_INTENT_HANDOFF && content.openInChrome;

  return (
    // z-50 e antes do container de toasts no DOM: o toast "Link copiado"
    // aparece por cima da folha.
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/40 md:items-center md:p-6"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      data-testid="install-guide-backdrop"
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="max-h-[85vh] w-full overflow-y-auto rounded-t-3xl border border-slate-200/80 bg-white p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] shadow-subtle focus:outline-none md:max-w-md md:rounded-3xl md:p-6"
      >
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary-50 text-primary-700">
            <FiDownload className="h-5 w-5" aria-hidden="true" />
          </div>
          <h2
            id={titleId}
            className="m-0 min-w-0 flex-1 self-center text-lg font-bold leading-6 text-slate-900"
          >
            Instalar {appName}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="-mr-2 -mt-2 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-slate-500 transition duration-150 hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-4 focus:ring-primary-100"
          >
            <FiX className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        {content.title ? (
          <p className="m-0 mt-4 text-sm font-semibold leading-6 text-slate-900">
            {content.title}
          </p>
        ) : null}

        <ol className="m-0 mt-4 list-none space-y-3 p-0">
          {content.steps.map((step, index) => {
            const Icon = INSTALL_STEP_ICONS[step.icon];
            return (
              <li key={step.text} className="flex items-center gap-3">
                <span
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-700"
                  data-icon={step.icon}
                  aria-hidden="true"
                >
                  <Icon className="h-5 w-5" />
                </span>
                <span
                  className="w-4 shrink-0 text-sm font-bold text-slate-400"
                  aria-hidden="true"
                >
                  {index + 1}
                </span>
                <p className="m-0 min-w-0 flex-1 text-sm leading-5 text-slate-700">
                  {step.text}
                </p>
              </li>
            );
          })}
        </ol>

        {content.copyLink || showOpenInChrome ? (
          <div className="mt-5 grid grid-cols-1 gap-2">
            {content.copyLink ? (
              <button
                type="button"
                onClick={() => void copyLink()}
                className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 text-sm font-bold text-white shadow-sm transition duration-150 hover:bg-primary-700 focus:outline-none focus:ring-4 focus:ring-primary-100"
              >
                <FiCopy className="h-4 w-4" aria-hidden="true" />
                Copiar link
              </button>
            ) : null}
            {showOpenInChrome ? (
              <a
                href={buildChromeIntentUrl()}
                className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 shadow-sm transition duration-150 hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-primary-100"
              >
                <FiExternalLink className="h-4 w-4" aria-hidden="true" />
                Abrir no Chrome
              </a>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
